import { useCallback, useRef, useState } from 'react'

type SingleFlightOptions = {
  /**
   * After a run completes, ignore further calls for this many milliseconds.
   * Guards synchronous handlers (e.g. a plain `window.open`) against rapid
   * repeat firing from a sticky/bouncing mouse button. Defaults to 0 (disabled).
   */
  cooldownMs?: number
}

type SingleFlightCallback<Args extends unknown[]> = {
  /** Runs the wrapped callback unless one is already in flight (or in cooldown). */
  run: (...args: Args) => Promise<void>
  /** True while a run is in flight — use to disable the trigger / show a busy state. */
  isRunning: boolean
}

/**
 * Wraps an (optionally async) callback so it can only run once at a time.
 *
 * While a call is in flight, further calls are ignored — so a handler that
 * fetches then opens a tab can't be fired several times before the first
 * completes (which would open several tabs / spike memory). An optional
 * `cooldownMs` additionally suppresses rapid repeat calls after completion,
 * which is what protects purely synchronous handlers.
 *
 * The in-flight guard is a ref (not state), so it takes effect synchronously
 * within the same event — before React re-renders — which is exactly the
 * window a `disabled`/state-based guard cannot cover.
 */
export function useSingleFlightCallback<Args extends unknown[]>(
  callback: (...args: Args) => void | Promise<void>,
  options: SingleFlightOptions = {},
): SingleFlightCallback<Args> {
  const { cooldownMs = 0 } = options

  const inFlightRef = useRef(false)
  const lastRunAtRef = useRef(0)
  const [isRunning, setIsRunning] = useState(false)

  const run = useCallback(
    async (...args: Args) => {
      if (inFlightRef.current) return
      if (cooldownMs > 0 && Date.now() - lastRunAtRef.current < cooldownMs) return

      inFlightRef.current = true
      setIsRunning(true)
      try {
        await callback(...args)
      } finally {
        inFlightRef.current = false
        lastRunAtRef.current = Date.now()
        setIsRunning(false)
      }
    },
    [callback, cooldownMs],
  )

  return { run, isRunning }
}
